<template>
  <IndicatorCard
    :loading="loading"
    :has-data="laborCosts.length > 0"
    :is-result="isResult"
    :no-data-message="$t('Nenhum dado encontrado')"
    :title="title"
    :description="indicator.description"
    :excel-endpoint="indicator.excelEndpoint"
    :hide-excel="!isResult"
    :excel-loading="excelLoading"
    @export-excel="requestExcel"
  >
    <template #date-range>
      <DateRangePicker
        :start-date="startDate"
        :end-date="endDate"
        :disabled="isResult"
        @change="onDateRangeChange"
      />
    </template>

    <v-row class="ma-0" justify="center">
      <v-col cols="12">
        <div>
          <div class="hidden-md-and-up">
            <v-data-table
              :loading="loading"
              :headers="headerMobile"
              :items="laborCosts"
              class="main-table"
              :hide-default-footer="!filterProp.data"
              show-expand
              item-key="userName"
              :single-expand="false"
              :items-per-page="7"
              :header-props="headerProps"
              :mobile-breakpoint="0"
              :expanded.sync="expanded"
              @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
            >
              <template v-slot:[`item.responsavel`]="{ item }">
                {{ item.userName }}
              </template>

              <template v-slot:[`item.custo`]="{ item }">
                {{ item.totalCostFormatted }}
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length">
                  <v-col class="ma-0 pa-0" cols="12">
                    <p>
                      <strong>{{ $t('Tempo Total') }}:</strong>
                      {{ item.totalTimeSpent }}
                    </p>
                  </v-col>
                  <v-col class="ma-0 pa-0" cols="12">
                    <p>
                      <strong>{{ $t('Valor Homem-Hora') }}:</strong>
                      {{
                        item.manHourRate != null
                          ? item.manHourRate.toInstLocale()
                          : '0,00'
                      }}
                    </p>
                  </v-col>
                </td>
              </template>
            </v-data-table>
            <div class="pagination" v-if="!filterProp.data">
              <div class="item-pagination block-two">
                <v-pagination
                  class="pagination-item"
                  v-model="page"
                  :length="totalPages"
                  :total-visible="9"
                ></v-pagination>
              </div>
            </div>
          </div>

          <div class="hidden-md-and-down">
            <v-data-table
              :loading="loading"
              :headers="header"
              :items="laborCosts"
              class="main-table"
              :hide-default-footer="!filterProp.data"
              hide-default-header
              :header-props="headerProps"
              :items-per-page="7"
            >
              <template #header="{ props, on }">
                <thead>
                  <tr>
                    <th
                      class="text-center"
                      v-for="h in props.headers"
                      :key="h.value"
                    >
                      <div>
                        <v-hover v-slot="{ hover }">
                          <div>
                            <span>{{ $t(h.text) }}</span>
                            <v-icon
                              v-if="
                                hover &&
                                h.value != 'data-table-select' &&
                                h.value != 'actions'
                              "
                              color="white"
                              class="mx-2"
                              @click="on.sort(h.value)"
                            >
                              {{
                                props.options.sortBy.includes(h.value)
                                  ? 'mdi-chevron-down'
                                  : 'mdi-chevron-up'
                              }}
                            </v-icon>
                          </div>
                        </v-hover>
                      </div>
                    </th>
                  </tr>
                </thead>
              </template>

              <template v-slot:[`item.responsavel`]="{ item }">
                {{ item.userName }}
              </template>

              <template v-slot:[`item.tempoTotal`]="{ item }">
                {{ item.totalTimeSpent }}
              </template>

              <template v-slot:[`item.valorHomemHora`]="{ item }">
                {{
                  item.manHourRate != null
                    ? item.manHourRate.toInstLocale()
                    : '0,00'
                }}
              </template>

              <template v-slot:[`item.custoTotal`]="{ item }">
                {{ item.totalCostFormatted }}
              </template>
            </v-data-table>
            <div class="pagination" v-if="!filterProp.data">
              <div class="item-pagination block-two">
                <v-pagination
                  class="pagination-item"
                  v-model="page"
                  :length="totalPages"
                  :total-visible="9"
                ></v-pagination>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <template #review>
      <v-row class="ma-0">
        <v-col cols="12" v-if="isResult && review">
          <strong>{{ $t('Análise crítica') }}</strong>
          <p v-html="review"></p>
        </v-col>
        <v-col cols="12" v-else-if="!isResult">
          <tiptap-vuetify
            v-model="filter.review"
            :extensions="extensions"
            :label="$t('Análise crítica')"
            :placeholder="$t('Digite sua análise crítica para o indicador abaixo')"
            :card-props="{ flat: true, outlined: true, rounded: true }"
            class="primary-input"
            outlined
            dense
            hide-details="auto"
          ></tiptap-vuetify>
        </v-col>
      </v-row>
    </template>
  </IndicatorCard>
</template>

<script>
import { getIndicatorById } from '../../../../../indicators/indicatorRegistry'
import ApiService from '../../../../../../../../services/ApiService'
import moment from 'moment'
import IndicatorCard from '../../shared/IndicatorCard.vue'
import DateRangePicker from '../../shared/DateRangePicker.vue'
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from 'tiptap-vuetify'

export default {
  props: {
    title: { type: String, default: '' },
    apiEndpointOverride: { type: String, default: '' },
    filterProp: Object,
    institution: Object,
    unidades: Array,
    isResult: { type: Boolean, default: false },
    review: String,
  },

  components: {
    IndicatorCard,
    DateRangePicker,
    TiptapVuetify,
  },

  data() {
    return {
      excelLoading: false,
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [Heading, { options: { levels: [1, 2, 3] } }],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak,
      ],
      filter: {
        page: 1,
        pageSize: 7,
        institutionId: null,
        unidadeIds: [],
        startDate: null,
        endDate: null,
        review: '',
      },
      apiService: new ApiService(),
      loading: true,
      page: 1,
      totalPages: 1,
      laborCosts: [],
      expanded: [],
      startDate: '',
      endDate: '',
      headerProps: { sortByText: 'Ordenar por' },
      header: [
        { text: 'Responsável', align: 'center', value: 'responsavel', sortable: false },
        { text: 'Tempo Total', align: 'center', value: 'tempoTotal', sortable: false },
        { text: 'Valor Homem-Hora (R$/h)', align: 'center', value: 'valorHomemHora', sortable: false },
        { text: 'Custo Total', align: 'center', value: 'custoTotal', sortable: false },
      ],
      headerMobile: [
        { text: 'Responsável', align: 'center', value: 'responsavel', sortable: false },
        { text: 'Custo Total', align: 'center', value: 'custo', sortable: false },
      ],
    }
  },

  async created() {
    this.filter.institutionId = this.institution.id
    this.filter.unidadeIds = this.unidades

    this.startDate = moment()
      .add(-1, 'month')
      .clone()
      .startOf('month')
      .format('YYYY-MM-DD')

    this.endDate = moment()
      .add(-1, 'month')
      .clone()
      .endOf('month')
      .format('YYYY-MM-DD')

    if (this.filterProp) {
      this.filter = this.filterProp
      this.startDate = moment(this.filter.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')

      this.endDate = moment(this.filter.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    }

    this.list()
  },

  computed: {
    indicator() {
      return getIndicatorById('internal_labor_cost') || {}
    },
  },

  methods: {
    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.filter.page = 1
      this.page = 1
      this.filterProp.data = null
      this.list()
    },

    requestExcel() {
      if (!this.indicator.excelEndpoint) return
      this.excelLoading = true
      const filename = `${this.indicator.legacyKey}.xlsx`
      this.apiService
        .post(this.indicator.excelEndpoint, { ...this.filter, pageSize: 999999 })
        .then((response) => {
          const data = `data:application/octet-stream;base64,${response.content}`
          const link = document.createElement('a')
          link.href = data
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .finally(() => {
          this.excelLoading = false
        })
    },

    setDates() {
      this.filter.startDate = moment(this.startDate)
        .clone()
        .startOf('month')
        .format('YYYY-MM-DD')

      this.filter.endDate = moment(this.endDate)
        .clone()
        .endOf('month')
        .format('YYYY-MM-DD')
    },

    setInfos() {
      const model = {
        chart: getIndicatorById('internal_labor_cost').legacyKey,
        ...this.filter,
        data: null,
      }
      this.$emit('setInfos', model)
    },

    async list() {
      this.loading = true
      try {
        if (this.filterProp.data) {
          const response = JSON.parse(this.filterProp.data)
          this.laborCosts = response.content.objects
          this.totalPages = response.content.pager.totalPages
          return
        }

        if (!this.isResult) this.setDates()
        this.filter.institutionId = this.institution.id
        this.filter.pageSize = 7
        this.filter.unidadeIds = this.unidades

        const response = await this.apiService.post(
          this.apiEndpointOverride || 'reports/internalLaborCost',
          this.filter
        )
        this.laborCosts = response.content.objects
        this.totalPages = response.content.pager.totalPages
      } finally {
        this.loading = false
      }
    },
  },

  watch: {
    page() {
      this.filter.page = this.page
      this.list()
    },

    filter: {
      deep: true,
      handler() {
        this.setInfos()
      },
    },
  },
}
</script>
